<template>
  <div>
    <template v-if="visible">
      <h3 class="input-panel__subhead panel-term__subhead"
        v-if="activeTerms.length === index"
      >Choose Term {{ index + 1 }}</h3>

      <h3 class="input-panel__subhead panel-term__subhead" 
        :class="{ 'panel-term__subhead--disabled': activeTerms.length < index }"
        v-else
      >Term {{ index + 1 }}</h3>
    </template>

    <template v-for="option in options" :key="option.id">
      <fieldset class="radio-button"
        v-if="option.value <= monthDiff && visible"
      >
        <input type="radio"  
          :name="radioSetId" 
          :value="option.value" 
          :id="option.id"
          :ref="option.id"
          :checked="option.value === value"
          :disabled="disableTerm(false, option.value)"
          @change="selectTerm(option.value, option.id)"
        >
        <label :for="option.id">{{ option.value }} Months</label>
      </fieldset>
    </template>
    
    <template v-if="allowCustomTerms && visible">
      <template v-if="!(value && value != 12 && value != 24 && value != 36)">
        <fieldset class="checkbox-button">
          <input type="checkbox"
            :id="customToggleId"
            :checked="customTerm"
            @change="toggleCustom"
          >
          <label :for="customToggleId">Custom Term</label>
        </fieldset>
      </template>
      
      <template v-if="value && value != 12 && value != 24 && value != 36">
        <fieldset class="radio-button">
          <input type="radio"  
            :name="'custom-term-radio-'+id" 
            :value="value" 
            :id="'custom-term-radio-'+id"
            checked
          >
          <label :for="'custom-term-radio-'+id">{{ value }} Months</label>
        </fieldset>
      </template>

      <template v-if="customTerm">

        <hr>
        <label :for="customTermId">Select Custom Term&nbsp;Length:</label>
        <select :id="customTermId"
          @change="selectTerm(parseInt($event.target.value))"
        >
          <option disabled :selected="!isCustom" value="0"> -- select -- </option>
          <option 
            v-for="customTerm in customTerms"
            :value="customTerm"
            :selected="checkCustom(customTerm)"
            :disabled="disableTerm(true, customTerm)"
          >{{ customTerm }} Month<template v-if="customTerm != 1">s</template></option>
        </select>
      </template>
    </template>

    <button class="btn btn-sm btn-icon mrgn-btm-10"
      v-if="value"
      @click.prevent="clearSelection"
    ><i class="fa fa-times"></i>&nbsp;Clear</button>
  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert'

let uuid = 0

export default {
  props: ['index', 'id', 'value'],
  data() {
    return {
      uuid: uuid,
      selection: '',
      options: [
        { 
          value: 12, 
          id: 'term-12--' + uuid
        },
        { 
          value: 24,
          id: 'term-24--' + uuid 
        },
        { 
          value: 36,
          id: 'term-36--' + uuid
        }
      ],
      radioSetId: 'term-length--' + uuid,
      customToggleId: 'custom-term--' + uuid,
      customTermId: 'custom-term-length--' + uuid,
      customTerm: false,
    }
  },
  beforeCreate() {
    uuid += 1;
  },
  computed: {
    startMonth() {
      // The start month timestamp set in the store
      if (this.$store.getters.startMonth) {
        return moment(this.$store.getters.startMonth)
      }
    },
    planConfig() {
      return this.$store.getters.planConfig
    },
    termLength() {
      // The terms set in the store
      return this.$store.getters.termLength
    },
    termLengthCount() {
      // The number of terms set in the store
      return Object.keys(this.$store.getters.termLength).length
    },
    activeTermsArray() {
      let activeTerms = this.termLength
      activeTerms = activeTerms.filter(function(item) {
        return item.value != ''
      })
      
      let activeTermsArray = activeTerms.map(a => parseInt(a.value));

      return activeTermsArray
    },
    activeTerms() {
      var activeTerms = this.termLength
      activeTerms = activeTerms.filter(function(item) {
        return item.value != ''
      })
      return activeTerms
    },
    longestTermLength() {   
      if (this.activeTerms.length > 0) {
        const longestTerm = this.activeTerms.sort(function(a, b) {
          if (parseInt(a.value) < parseInt(b.value)) {
            return 1;
          } else {
            return -1;
          }
        })

        let val = parseInt(longestTerm[0].value)

        // If we're updating a temporary custom profile,
        // then use the longest selected term.
        if (this.tempCustomFacilityProfile.months) {
          return val

        // or if we're updating a saved custom profile,
        // use the longest term from the profile.
        } else if (
          this.customFacilityProfile.months && 
          this.customFacilityProfile.months.length > 0 && 
          this.customFacilityProfile.months.length > val
        ) {
          return this.customFacilityProfile.months.length
        
        } else {
          return val
        }

      } else if (
        this.customFacilityProfile.months && 
        this.customFacilityProfile.months.length > 0
      ) {
        return this.customFacilityProfile.months.length
      }
    },
    selectedOption() {
      return this.options.find(term => term.value == this.value)
    },
    monthRange() {
      let startMonth = new moment(this.startMonth)
      let endMonth = new moment(this.startMonth).add(this.longestTermLength, 'months').date(0)
      let months = []

      while (endMonth > startMonth) {
        months.push(startMonth.format('YYYY-MM-DD'))
        startMonth.add(1,'month')
      }

      return months
    },
    monthDiff() {
      // Used to limit available terms based on start month
      if (this.startMonth) {
        const now = moment()
        const diff = 60 - this.startMonth.diff(now, 'months')
        return diff
      }
    },
    allowCustomTerms() {
      return this.planConfig.allowCustomTerms
    },
    isCustom() {
      if (this.value && this.customTerms.indexOf(parseInt(this.value)) < 0) {
        this.customTerm = false
      }

      if (this.value && this.value != 12 && this.value != 24 && this.value != 36) {
        return true
      }

      return false
    },
    customTerms() {
      // Generate the possible custom terms
      if (this.startMonth) {
        const diff = this.monthDiff

        let terms = []
        
        for (let i = 0; i < diff; i++) {
          terms[i] = i + 1
        } 
        
        let skip = [12,24,36]
        skip = skip.concat(this.activeTermsArray)

        terms = terms.filter(function(item) {
          return skip.indexOf(item) === -1
        })

        return terms
      }
    },
    customFacilityProfile() {
      return this.$store.getters.customFacilityProfile
    },
    tempCustomFacilityProfile() {
      return this.$store.getters.tempCustomFacilityProfile
    },
    customFacilityProfileSet() {
      if (Object.keys(this.customFacilityProfile).length > 0) {
        return true
      }

      return false
    },
    tempCustomFacilityProfileSet() {
      if (Object.keys(this.tempCustomFacilityProfile).length > 0) {
        return true
      }

      return false
    },
    validSelectionsRemaining() {
      if (!this.allowCustomTerms) {
        let options = [12, 24, 36]
        let selectedOptions = this.termLength.filter(term => (term.value === 12 || term.value === 24 || term.value === 36))
        
        if (selectedOptions.length) {
          for (let x = 0; x < selectedOptions.length; ++x) {
            options = options.filter(option => option != selectedOptions[x].value)
          }
        }

        options = options.filter(option => option <= this.monthDiff)

        return options.length
      }

      return true
    },
    visible() {
      if (!this.allowCustomTerms) {

        if (this.value) {
          return true

        } else if (this.validSelectionsRemaining) {
          return true
        }

        return false

      } else {

        if (this.isCustom) { 
          return true
        }

        if (this.customTerms && this.customTerms.length < 1) {
          return false
        }
      }

      return true
    }
  },
  methods: {
    selectTerm(value, id) {
      this.selection = value
      
      let obj = []
      if (this.termLengthCount < 1) {

        // Term length is not set in the store
        obj[0] = {
          id: this.id,
          value: value
        }

      } else {
        // Term length is set in the store.
        let prevTermLength = this.termLength

        // Has this index been set?
        if (this.termLength[this.index]) {

          // This ID has been set, so replace its value
          for (let i = 0; i < this.termLengthCount; ++i) {
            if (this.termLength[i].id === this.id) {
              obj[i] = {
                id: this.id,
                value: value
              }
            } else {
              obj[i] = {
                id: this.termLength[i].id,
                value: this.termLength[i].value
              }
            }     
          }
        }
      }

      this.$store.dispatch('setTermLength', obj)
      this.$emit('selectTerm', this.index) 

      if (this.customFacilityProfileSet) {
        // A previous custom profile exists,
        // so we need to update the month values accordingly
        // for the new term length
        this.updateCustomFacilityProfile(false)
          
      } else if (this.tempCustomFacilityProfileSet) {
        // A previous custom profile was started but not submitted,
        // so we need to update the month values accordingly
        // for the new term length
        this.updateCustomFacilityProfile(true)
      }
    },
    toggleCustom() {
      this.clearSelection(true)
      this.customTerm = !this.customTerm
    },
    checkCustom(term) {  
      return (this.value * 1) === term
    },
    disableTerm(custom, value) {
      // If in custom term entry mode
      if (!custom && this.customTerm === true) {
        // return true
      }

      for (let i = 0; i < this.termLengthCount; ++i) {
        if (this.termLength[i].id != this.id) {
          if (this.termLength[i].value === value) {
            return true
          }
        }
      }

      return false
    },   
    clearSelection(skipIndexRemoval) {
      this.customTerm = false
      let obj = []
      for (let i = 0; i < this.termLengthCount; ++i) {
        if (this.termLength[i].id === this.id) {
          obj[i] = {
            id: this.id,
            value: ''
          }
        } else {
          obj[i] = {
            id: this.termLength[i].id,
            value: this.termLength[i].value
          }
        }     
      }

      this.$store.dispatch('setTermLength', obj)

      if (!skipIndexRemoval || typeof skipIndexRemoval != 'boolean') {
        this.$emit('deselectTerm', this.index, this.id)
      }

      if (this.customFacilityProfileSet) {
        // A previous custom profile exists,
        // so we need to update the month values accordingly
        // for the new term length
        this.updateCustomFacilityProfile(false)
          
      } else if (this.tempCustomFacilityProfileSet) {
        // A previous custom profile was started but not submitted,
        // so we need to update the month values accordingly
        // for the new term length
        this.updateCustomFacilityProfile(true)
      }
    },
    updateCustomFacilityProfile(isTemp) {

      const hasCfpMonths = (isTemp) 
        ? this.tempCustomFacilityProfile.months.length > 0 
        : this.customFacilityProfile.months.length > 0

      if (this.longestTermLength && hasCfpMonths) {

        // Transfer Month Values:
        let months = []

        for (let i = 0; i < this.monthRange.length; ++i) {

          if (i <= this.longestTermLength) {

            let month = {}
            let previousMonth

            if (isTemp) {
              previousMonth = this.tempCustomFacilityProfile.months[i]
            } else {
              previousMonth = this.customFacilityProfile.months[i]
            }

            if (previousMonth) {

              month = {
                month: previousMonth.month,
                usage: previousMonth.usage,
                onPeakUsage: previousMonth.onPeakUsage,
                onPeakUsagePercent: previousMonth.onPeakUsagePercent,
                offPeakUsage: previousMonth.offPeakUsage,
                peakDemand: previousMonth.peakDemand
              }
            } else {

              month = {
                month: this.monthRange[i],
                usage: '',
                onPeakUsage: '',
                onPeakUsagePercent: null,
                offPeakUsage: '',
                peakDemand: ''
              }
            }

            months.push(month)
          }
        }

        // Transfer Extra Column Values:
        let previousExtraColumns = (isTemp) 
          ? this.tempCustomFacilityProfile.extraColumns
          : this.customFacilityProfile.extraColumns

        let extraColumns = []

        if (previousExtraColumns.length > 0) {

          for (let i = 0; i < previousExtraColumns.length; ++i) {

            let months = []
            let valueRanges = []

            for (let x = 0; x < this.monthRange.length; ++x) {

              if (i <= this.longestTermLength) {

                let previousMonth = previousExtraColumns[i].months[x]
                let month = {}

                if (previousMonth) {

                  month = {
                    month: previousMonth.month,
                    value: previousMonth.value
                  }

                } else {

                  month = {
                    month: this.monthRange[x],
                    value: 0
                  }
                }

                months.push(month)
              }
            }

            if (previousExtraColumns[i].valueRanges != null && previousExtraColumns[i].valueRanges.length > 0) {

              let newStartMonth = new moment(this.startMonth).toDate()
              let newEndMonth = new moment(this.startMonth).add(this.longestTermLength, 'months').date(0).toDate()

              for (let y = 0; y < previousExtraColumns[i].valueRanges.length; ++y) {

                // Inspect each value range
                let previousStartMonth = new moment(previousExtraColumns[i].valueRanges[y].startMonth).toDate()
                let previousEndMonth = new moment(previousExtraColumns[i].valueRanges[y].endMonth).toDate()

                // If the value range falls within the new overall range, save it
                if (moment(previousStartMonth).isBetween(newStartMonth, newEndMonth) && moment(previousEndMonth).isBetween(newStartMonth, newEndMonth)) {

                  let range = {
                    value: previousExtraColumns[i].valueRanges[y].value,
                    startMonth: previousExtraColumns[i].valueRanges[y].startMonth,
                    endMonth: previousExtraColumns[i].valueRanges[y].endMonth
                  }

                  valueRanges.push(range)

                  continue
                }

                // If the old range straddles new start month, set range start month equal to new start month
                if (moment(previousStartMonth).isBefore(newStartMonth) && moment(previousEndMonth).isAfter(newStartMonth)) {

                  let range = {
                    value: previousExtraColumns[i].valueRanges[y].value,
                    startMonth: newStartMonth,
                    endMonth: previousExtraColumns[i].valueRanges[y].endMonth
                  }

                  valueRanges.push(range)

                  continue
                }

                // If the old range straddles new end month, set range end month equal to new end month
                if (moment(previousStartMonth).isBefore(newEndMonth) && moment(previousEndMonth).isAfter(newEndMonth)) {

                  let range = {
                    value: previousExtraColumns[i].valueRanges[y].value,
                    startMonth: previousExtraColumns[i].valueRanges[y].startMonth,
                    endMonth: newEndMonth
                  }

                  valueRanges.push(range)

                  continue
                }
              }
            }

            extraColumns[i] = {
              id: previousExtraColumns[i].id, 
              name: previousExtraColumns[i].name, 
              abbrev: previousExtraColumns[i].abbrev, 
              type: previousExtraColumns[i].type,
              months: months,
              valueRanges: valueRanges
            }
          }
        }

        // Recalculate Total:
        let total = {
          usage: 0,
          onPeakUsage: 0,
          offPeakUsage: 0
        }

        for (let i = 0; i < months.length; ++i) {
          if (months[i].usage) {
            total.usage = total.usage + parseInt(months[i].usage)
          }

          if (months[i].onPeakUsage) {
            total.onPeakUsage = total.onPeakUsage + parseInt(months[i].onPeakUsage)
          }

          if (months[i].offPeakUsage) {
            total.offPeakUsage = total.offPeakUsage + parseInt(months[i].offPeakUsage)
          }
        }

        // Update Profile:
        let saveAction = (isTemp)
          ? 'setTempCustomFacilityProfile' 
          : 'setCustomFacilityProfile'

        this.$store.dispatch(saveAction, {
          months: months,
          total: total,
          extraColumns: extraColumns
        })
      }
    }
  }
}
</script>
